import { useEffect, useMemo, useState } from 'react';
import {
  applyMentionPick,
  detectMentionTrigger,
  filterAgentsByMention,
  mentionToken,
  type MentionAgent,
  type PendingMention,
} from '../../../../lib/agent-mentions';

export interface AgentMentionsState {
  /** The panel is showing — a live `@` trigger that wasn't Escape-dismissed. */
  open: boolean;
  /** Roster filtered by what was typed after the `@`. */
  agents: MentionAgent[];
  /** Insert `@AgentName ` at the trigger and remember the structured id (a second pick replaces). */
  pick: (agent: MentionAgent) => void;
  /** Escape: hide the panel for THIS trigger; a fresh `@` reopens it. */
  dismiss: () => void;
  /** The one mention this turn will carry (v1: single; shaped for a list later). */
  pending: PendingMention | null;
  clear: () => void;
}

// The composer's @-mention state. The trigger derives from the draft text (a trailing
// `@query` at a word boundary); picking rewrites the draft and stores the pending id,
// which rides the next send as `references.mentionedAgentId`. Pending state resets on
// conversation switch and drops itself the moment its literal token leaves the text
// (covers both manual deletion and the composer clearing).
export function useAgentMentions({
  inputText,
  setInputText,
  conversationId,
  agents,
}: {
  inputText: string;
  setInputText: (v: string) => void;
  conversationId: string | null;
  agents: MentionAgent[];
}): AgentMentionsState {
  const [pending, setPending] = useState<PendingMention | null>(null);
  const [dismissedAt, setDismissedAt] = useState<number | null>(null);

  useEffect(() => {
    setPending(null);
    setDismissedAt(null);
  }, [conversationId]);

  useEffect(() => {
    if (pending && !inputText.includes(mentionToken(pending.name))) setPending(null);
  }, [inputText, pending]);

  const trigger = useMemo(() => detectMentionTrigger(inputText), [inputText]);

  // An Escape-dismissal is keyed to the trigger's position: typing more keeps it
  // dismissed; deleting back past the `@` (trigger gone) re-arms it.
  useEffect(() => {
    if (!trigger && dismissedAt !== null) setDismissedAt(null);
  }, [trigger, dismissedAt]);

  const filtered = useMemo(
    () => (trigger ? filterAgentsByMention(agents, trigger.query) : []),
    [agents, trigger],
  );

  const pick = (agent: MentionAgent) => {
    if (!trigger) return;
    setInputText(applyMentionPick(inputText, trigger.start, agent.name));
    setPending({ id: agent.id, name: agent.name });
  };

  return {
    open: !!trigger && trigger.start !== dismissedAt,
    agents: filtered,
    pick,
    dismiss: () => {
      if (trigger) setDismissedAt(trigger.start);
    },
    pending,
    clear: () => setPending(null),
  };
}
